import { useEffect } from "react";

/**
 * Lightweight SEO helper: updates document.title, meta description,
 * canonical, Open Graph, and injects JSON-LD schema.
 */
export default function SEO({
  title,
  description,
  canonical,
  ogImage,
  schema,
}) {
  useEffect(() => {
    if (title) document.title = title;

    const ensureMeta = (selector, attr, value, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    if (description) {
      ensureMeta(
        'meta[name="description"]',
        "name",
        "description",
        description,
      );
      ensureMeta(
        'meta[property="og:description"]',
        "property",
        "og:description",
        description,
      );
      ensureMeta(
        'meta[name="twitter:description"]',
        "name",
        "twitter:description",
        description,
      );
    }
    if (title) {
      ensureMeta('meta[property="og:title"]', "property", "og:title", title);
      ensureMeta(
        'meta[name="twitter:title"]',
        "name",
        "twitter:title",
        title,
      );
    }
    ensureMeta(
      'meta[property="og:type"]',
      "property",
      "og:type",
      "website",
    );
    ensureMeta(
      'meta[property="og:site_name"]',
      "property",
      "og:site_name",
      "GTA Garage Doors & Locksmith",
    );
    ensureMeta(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
      "summary_large_image",
    );

    if (ogImage) {
      ensureMeta(
        'meta[property="og:image"]',
        "property",
        "og:image",
        ogImage,
      );
      ensureMeta(
        'meta[name="twitter:image"]',
        "name",
        "twitter:image",
        ogImage,
      );
    }

    // Canonical
    const path = canonical || window.location.pathname;
    const fullCanonical = `https://gta-garagedoors.com${path === "/" ? "" : path}`;
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", fullCanonical);
    ensureMeta(
      'meta[property="og:url"]',
      "property",
      "og:url",
      fullCanonical,
    );

    // Hreflang en-CA
    let hreflang = document.head.querySelector('link[rel="alternate"][hreflang="en-CA"]');
    if (!hreflang) {
      hreflang = document.createElement("link");
      hreflang.setAttribute("rel", "alternate");
      hreflang.setAttribute("hreflang", "en-CA");
      document.head.appendChild(hreflang);
    }
    hreflang.setAttribute("href", fullCanonical);

    // Schema JSON-LD
    const oldSchema = document.getElementById("page-schema");
    if (oldSchema) oldSchema.remove();
    if (schema) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "page-schema";
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
    }

    window.scrollTo({ top: 0, behavior: "instant" });
  }, [title, description, canonical, ogImage, schema]);

  return null;
}
